import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from '../../utils/axiosBaseQuery';
import {
    AssetAvailabilityDateRange,
    CreateRoomRentalBookingPayload,
    DashboardDateRange,
    DashboardStatsResponse,
    EditRoomRentalBookingPayload,
    RentalBookingDetails,
} from '../../types/bookingCreation';
import { AssetAvailability } from '../types';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import type { CustomerMailRefusal } from '../../utils/customerMailGuard';

/**
 * The statuses an operator can SET by hand.
 *
 * Deliberately not widened with the money-return values: `refunded` and
 * `disputed` are derived from the ledger by the backend and would be
 * overwritten by the next refund event, so offering them as a manual choice
 * would be offering a setting that does not stick.
 */
export type PaymentStatusType = 'paid' | 'unpaid' | 'partially_paid' | 'overpaid';

/**
 * The statuses that can COME BACK from the API.
 *
 * A superset of the writable ones: the backend derives `refunded`,
 * `partially_refunded` and `disputed` from the money ledger when money goes
 * back (#2002). `StatusBadge` renders all of them.
 */
export type DerivedPaymentStatusType =
    | PaymentStatusType
    | 'refunded'
    | 'partially_refunded'
    | 'disputed'
    | 'pending';

/**
 * The approve-only answer (`backend/.../admin/order/approveOnly.ts`). The
 * approval is recorded whether or not the mail then went out (PO,
 * 2026-10-24); `mailSent` says which.
 */
export interface ApproveRoomRentalBookingResponse {
    approved: boolean;
    mailSent: boolean;
    mailResult: 'sent' | 'failed' | 'skipped' | 'blocked';
    /** Why the payment rule refused the mail (PO, 2026-10-30); the approval stands. */
    mailBlocked?: CustomerMailRefusal;
}

export const bookingApiSlice = createApi({
    reducerPath: 'bookingApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: axiosBaseQuery(),
    tagTypes: ['bookingList', 'bookingDetails', 'dashboardData', 'assetAvailabilityData'],
    endpoints: (builder) => ({
        createRoomRentalBooking: builder.mutation({
            query: ({ data, saveMode }: { data: CreateRoomRentalBookingPayload, saveMode: 'save_only' | 'save_and_send_email' }) => {
                return {
                    url: `/order/room-rental?saveMode=${saveMode}`,
                    method: 'POST',
                    data: data,
                };
            },
            // dashboardData: entering a booking moves the dashboard's figures,
            // the awaiting-approval count (M8, #1809) among them.
            invalidatesTags: ['bookingList', 'dashboardData'],
        }),
        // Non-room create/cancel/edit/delete live on the new (ECS) backend in
        // `bookingWriteApiSlice` — the storage create and the soft-cancel /
        // hard-delete mutations moved there (Step 2 booking write-path port).
        editRoomRentalBooking: builder.mutation({
            query: ({ orderId, data, saveMode }: { orderId: string, data: EditRoomRentalBookingPayload, saveMode: 'save_only' | 'save_and_send_email' }) => {
                return {
                    url: `/order/${orderId}/room-rental?saveMode=${saveMode}`,
                    method: 'POST',
                    data: data,
                };
            },
            // dashboardData: "save and send email" approves (M8, #1809).
            invalidatesTags: ['bookingList', 'bookingDetails', 'dashboardData'],
        }),
        // #3016 bulk approval: the edit route's approve-only mode. It sends the
        // mail "save and send email" sends and records the approval, without
        // editing the booking; 409 `not_awaiting_approval` for anything else.
        // No tags: the bulk run invalidates once when it has finished, rather
        // than refetching the whole list after every booking.
        approveRoomRentalBooking: builder.mutation<ApproveRoomRentalBookingResponse, { orderId: string }>({
            query: ({ orderId }) => ({
                url: `/order/${orderId}/room-rental?saveMode=save_and_send_email`,
                method: 'POST',
                data: { approveOnly: true },
            }),
        }),
        getBookingList: builder.query({
            query: (arg: string | { useCase: string, includeArchived?: boolean }) => {
                const useCase = typeof arg === 'string' ? arg : arg.useCase;
                const includeArchived = typeof arg === 'string' ? false : !!arg.includeArchived;
                return {
                    url: `/rental/bookings/${useCase}`,
                    ...(includeArchived ? { params: { includeArchived: true } } : {}),
                };
            },
            providesTags: ['bookingList'],
        }),
        getBookingDetails: builder.query<RentalBookingDetails, { orderId: string }>({
            query: ({ orderId }: { orderId: string }) => {
                return {
                    url: `/order/${orderId}/room-rental`,
                };
            },
            providesTags: ['bookingDetails', 'bookingList'],
        }),
        updateSetupTime: builder.mutation<void, { bookingId: string, setupTime: string }>({
            query: ({ bookingId, setupTime }) => ({
                url: `/order/${bookingId}/setup-time`,
                method: 'POST',
                data: { setupTime },
            }),
        }),
        finishRental: builder.mutation<void, { orderId: string, orderItems: string[], actorEmail?: string }>({
            query: ({ orderId, orderItems, actorEmail }) => ({
                url: 'rental/finish',
                method: 'POST',
                data: {
                    orderId,
                    orderItems,
                    actorEmail,
                },
            }),
            invalidatesTags: ['bookingDetails', 'bookingList'],
        }),
        getDashboardData: builder.query<DashboardStatsResponse, { data: DashboardDateRange }>({
            query: ({ data }: { data: DashboardDateRange }) => {
                return {
                    url: `/dashboard/${data.from}/${data.to}`,
                };
            },
            providesTags: ['dashboardData'],
        }),
        getAssetsAvailabilityData: builder.query<AssetAvailability[], { data: AssetAvailabilityDateRange }>({
            query: ({ data }: { data: AssetAvailabilityDateRange }) => {
                return {
                    url: `/asset/statistics/${data.year}/${data.month}`,
                };
            },
            providesTags: ['assetAvailabilityData'],
        }),
    }),
});

export const {
    useCreateRoomRentalBookingMutation,
    useEditRoomRentalBookingMutation,
    useGetBookingListQuery,
    useGetBookingDetailsQuery,
    useLazyGetBookingDetailsQuery,
    useUpdateSetupTimeMutation,
    useFinishRentalMutation,
    useGetDashboardDataQuery,
    useGetAssetsAvailabilityDataQuery,
} = bookingApiSlice;
